import { useNavigate } from "react-router-dom"
import { format } from "date-fns"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"

import { ApiTypes } from "types"
import { ReactComponent as CheckCircleIcon } from "assets/svg/notifications/icon-check-circle.svg"
import { ReactComponent as EnvelopeIcon } from "assets/svg/notifications/icon-envelope.svg"
import { ReactComponent as ExclamationTriangleIcon } from "assets/svg/notifications/icon-exclamation-triangle.svg"

dayjs.extend(utc)

import {
  AvatarWrapper,
  CardContainer,
  ContentWrapper,
  IconContainer,
  IssueTest,
  NotificationContainer,
  NotificationContent,
  ReadIndicator,
  StyledTooltip,
  Subtext,
  SubtextLine,
  TimeStamp,
  Title,
} from "./styled"

interface NotificationCardProps {
  notification: ApiTypes.Model.Notification
  onToggleRead: (id: number, isRead: boolean) => void
  onCardClick?: () => void
}

export const NotificationCard = ({ notification, onToggleRead, onCardClick }: NotificationCardProps) => {
  const navigate = useNavigate()

  const getAvatarType = (): "email" | "positive" | "warning" => {
    if ([1, 2, 4].includes(notification.event_type_id)) {
      return "warning"
    }

    if ([3, 5].includes(notification.event_type_id)) {
      return "positive"
    }

    return "email"
  }

  const getAvatarIcon = (type: "email" | "positive" | "warning") => {
    switch (type) {
      case "positive":
        return <CheckCircleIcon />
      case "warning":
        return <ExclamationTriangleIcon />
      default:
        return <EnvelopeIcon />
    }
  }

  const formatTimeAgo = (dateString: string) => {
    const utcDate = dayjs.utc(dateString)
    const localDate = utcDate.local()
    const now = dayjs()
    const diffInSeconds = Math.floor(now.diff(localDate, "second"))

    if (diffInSeconds < 60) return "Just now"
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`

    return format(localDate.toDate(), "MMM d, yyyy")
  }

  const handleToggleRead = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    onToggleRead(notification.id, notification.is_read)
  }

  const getTitle = () => {
    if (notification.event_type_id === 2) {
      return <div>An alert has been triggered on a device. Check the Monitoring page for details.</div>
    }
    if (notification.event_type_id === 4) {
      return (
        <div>
          Performance Test Result: <IssueTest>Issue</IssueTest>
          <div>View the test result details on Monitoring page</div>
        </div>
      )
    }

    return notification.subject
  }

  const renderAlertContent = () => {
    const eventParams = notification.event_params

    switch (notification.event_type_id) {
      case 1:
      case 2:
        return (
          <>
            <SubtextLine>Alert Type: {eventParams.alert_type}</SubtextLine>
            <SubtextLine>Customer Email: {eventParams.customer_email}</SubtextLine>
            <SubtextLine>Serial Number: {eventParams.device_sn}</SubtextLine>
          </>
        )
      case 5:
        return (
          <>
            <SubtextLine>New Plan: Nuve {eventParams.subscription_plan}</SubtextLine>
          </>
        )

      default:
        return (
          <>
            <SubtextLine>Customer Email: {eventParams.customer_email}</SubtextLine>
            <SubtextLine>Serial Number: {eventParams.device_sn}</SubtextLine>
          </>
        )
    }
  }

  const handleNotificationCardClick = () => {
    if (notification.event_type_id === 5) {
      return
    }

    if (!notification.is_read) {
      onToggleRead(notification.id, notification.is_read)
    }

    if (notification.event_type_id === 1) {
      navigate(
        `/alerts?sn=${notification.event_params.device_sn}&type=${notification.event_params.alert_type?.toLowerCase().replaceAll(" ", "_")}`,
      )
    }

    if (notification.event_type_id === 2) {
      navigate(
        `/monitoring?sn=${notification.event_params.device_sn}&type=efficiency_issue&custom_param=${notification.event_params.base64_custom_param}`,
      )
    }

    if (notification.event_type_id === 3) {
      navigate(
        `/customers/${notification.event_params.customer_id}/overview/?selectedDevice=${notification.event_params.device_sn}`,
      )
    }

    if (notification.event_type_id === 4) {
      navigate(
        `/monitoring?sn=${notification.event_params.device_sn}&testId=${notification.event_params.pertfest_result_id}`,
      )
    }

    if (notification.event_type_id === 6) {
      navigate(`/monitoring?sn=${notification.event_params.device_sn}&openOffline=true`)
    }

    onCardClick?.()
  }

  const avatarType = getAvatarType()
  const tooltipId = `notification-tooltip-${notification.id}`
  const tooltipText = notification.is_read ? "Mark as unread" : "Mark as read"

  return (
    <CardContainer
      isRead={notification.is_read}
      isClickable={notification.event_type_id !== 5}
      onClick={handleNotificationCardClick}
    >
      <NotificationContainer>
        <AvatarWrapper type={avatarType}>{getAvatarIcon(avatarType)}</AvatarWrapper>
        <ContentWrapper>
          <NotificationContent>
            <Title>{getTitle()}</Title>
            <Subtext>{renderAlertContent()}</Subtext>
          </NotificationContent>
          <TimeStamp>{formatTimeAgo(notification.created_at)}</TimeStamp>
        </ContentWrapper>
      </NotificationContainer>
      <IconContainer isRead={notification.is_read} onClickCapture={handleToggleRead} data-tooltip-id={tooltipId}>
        <ReadIndicator isRead={notification.is_read} />
      </IconContainer>
      <StyledTooltip id={tooltipId} variant="dark" orientation="bottom-end" delay={500}>
        {tooltipText}
      </StyledTooltip>
    </CardContainer>
  )
}
